import React, { useMemo, useCallback } from "react";

import {
  ActionIcon,
  Button,
  Center,
  Divider,
  Group,
  Indicator,
  Popover,
  ScrollArea,
  Stack,
  Text
} from "@mantine/core";

import { TbBell } from "react-icons/tb";

import NotificationItem from "./NotificationItem";

import { useNotifications } from "../../hooks/useNotifications";
import { useMarkNotificationsAsVisitedMutation } from "../../hooks/mutations/useMarkNotificationsAsVisitedMutation";
import { useMarkNotificationsAsReadMutation } from "../../hooks/mutations/useMarkNotificationsAsReadMutation";

const NotificationsDropdown = () => {
  const {
    oldNotifications,
    newNotifications,
    unreadNotifications,
    unvisitedNotifications
  } = useNotifications();

  const { markAsVisited, isLoading: isMarkAsVisitedLoading } =
    useMarkNotificationsAsVisitedMutation();

  const { markAsRead } = useMarkNotificationsAsReadMutation();

  const unvisitedNotificationsIds = useMemo(
    () => unvisitedNotifications.map(notification => notification.id),
    [unvisitedNotifications]
  );

  const unreadNotificationsIds = useMemo(
    () => unreadNotifications.map(notification => notification.id),
    [unreadNotifications]
  );

  const clickHandler = useCallback(() => {
    if (unreadNotifications.length > 0) {
      markAsRead(unreadNotificationsIds);
    }
  }, [unreadNotifications.length, unreadNotificationsIds, markAsRead]);

  const isAllVisited = unvisitedNotifications.length === 0;

  return (
    <Popover withArrow shadow="md" width={460}>
      <Popover.Target>
        <Indicator
          inline
          color="red"
          offset={4}
          disabled={!unreadNotifications.length}
          label={
            <Text fz={9} fw={600}>
              {unreadNotifications.length > 99
                ? "99+"
                : unreadNotifications.length}
            </Text>
          }
          size={16}
        >
          <ActionIcon onClick={clickHandler} c="dark.2">
            <TbBell size={20} />
          </ActionIcon>
        </Indicator>
      </Popover.Target>

      <Popover.Dropdown>
        <Stack>
          <Group justify="space-between">
            <Text fz="xs" fw="bold">
              Notifications
            </Text>

            <Button
              variant="subtle"
              size="xs"
              disabled={isMarkAsVisitedLoading || isAllVisited}
              onClick={() => {
                markAsVisited(unvisitedNotificationsIds);
              }}
            >
              Mark all as read
            </Button>
          </Group>

          <ScrollArea.Autosize
            mah={350}
            type="always"
            scrollbars="y"
            offsetScrollbars
          >
            <Stack gap="lg">
              <div>
                <Divider
                  label="New Notifications"
                  labelPosition="left"
                  mb="xs"
                />

                {newNotifications.length > 0 && (
                  <Stack gap="xs">
                    {newNotifications.map(item => (
                      <NotificationItem
                        key={item.id}
                        item={item}
                        onClick={() => {
                          markAsVisited([item.id]);
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </div>

              <div>
                <Divider
                  label="Earlier Notifications"
                  labelPosition="left"
                  mb="xs"
                />

                {oldNotifications.length > 0 && (
                  <Stack gap="xs">
                    {oldNotifications.map(item => (
                      <NotificationItem
                        key={item.id}
                        item={item}
                        onClick={() => {
                          markAsVisited([item.id]);
                        }}
                      />
                    ))}
                  </Stack>
                )}
              </div>
            </Stack>

            {newNotifications.length === 0 && oldNotifications.length === 0 && (
              <Center ta="center" py="md">
                <Text c="dimmed">No notifications yet</Text>
              </Center>
            )}
          </ScrollArea.Autosize>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
};

export default React.memo(NotificationsDropdown);
